import { z } from 'zod';
/**
 * Explainable customer-health scoring.
 *
 * The score is advisory only. It exists to focus human attention, never to
 * act: by contract there is NO code path from a health score to suspension,
 * billing, entitlement change or any other write against the customer.
 * Every score ships with its inputs, weights, confidence and freshness, and
 * supports manual review that overrides the computed band with a reason.
 */
export const HEALTH_FACTOR_KEYS = [
    'onboarding_progress',
    'support_burden',
    'sla_experience',
    'connectivity',
    'payment_standing',
    'release_currency',
];
export const HealthFactorKeySchema = z.enum(HEALTH_FACTOR_KEYS);
/** Fixed, visible weights. Changing them is a reviewed contracts change. */
export const HEALTH_FACTOR_WEIGHTS = {
    onboarding_progress: 0.15,
    support_burden: 0.2,
    sla_experience: 0.15,
    connectivity: 0.2,
    payment_standing: 0.2,
    release_currency: 0.1,
};
export const HEALTH_BANDS = ['healthy', 'watch', 'at_risk'];
export const HealthBandSchema = z.enum(HEALTH_BANDS);
/** Band thresholds on the 0–100 composite. */
export const HEALTH_BAND_THRESHOLDS = { healthy: 70, watch: 45 };
export function bandForScore(score) {
    if (score >= HEALTH_BAND_THRESHOLDS.healthy)
        return 'healthy';
    if (score >= HEALTH_BAND_THRESHOLDS.watch)
        return 'watch';
    return 'at_risk';
}
export const HealthFactorSchema = z.object({
    key: HealthFactorKeySchema,
    title: z.string(),
    /** 0–100 for this factor. Null when the source has no data. */
    score: z.number().min(0).max(100).nullable(),
    weight: z.number(),
    /** Plain-language statement of what produced this factor score. */
    basis: z.string(),
    /** When the underlying source was last updated. */
    sourceFreshness: z.iso.datetime().nullable(),
});
export const HealthScoreSchema = z.object({
    organisationId: z.uuid(),
    organisationName: z.string().optional(),
    /** Weighted composite over factors WITH data, 0–100. */
    score: z.number().min(0).max(100).nullable(),
    band: HealthBandSchema.nullable(),
    factors: z.array(HealthFactorSchema),
    /** Share of factor weight that had data behind it (0–1). */
    confidence: z.number().min(0).max(1),
    computedAt: z.iso.datetime(),
    /** Manual review overlay — a human judgement recorded beside the number. */
    reviewBand: HealthBandSchema.nullable(),
    reviewNote: z.string().nullable(),
    reviewedByDisplayName: z.string().nullable(),
    reviewedAt: z.iso.datetime().nullable(),
});
export const HealthReviewInputSchema = z
    .object({
    band: HealthBandSchema,
    note: z.string().min(10).max(1000),
})
    .strict();
export const HealthListResponseSchema = z.object({
    items: z.array(HealthScoreSchema),
    computedAt: z.iso.datetime().nullable(),
});
//# sourceMappingURL=health.js.map